import React, { useEffect, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { useRoute } from '@react-navigation/native';
import {
  VictoryLine, VictoryAxis, VictoryChart, VictoryLabel, VictoryVoronoiContainer,
} from 'victory';
import Colors from '../constants/Colors';
import { comparisonLineColor } from '../components/Home/HomeGraphLineSelector';
import {
  axisDateLabel, tooltipDateLabel, tickValSamples, ChartDataPoint,
} from './axisUtils';
import { AggregationFunction } from '../requests/Sensors/GetMetric';

export const selectedLineColor: string = Colors.light.hereworksDarkBlue;
export const alternateLineColor: string = Colors.light.hereworksPurple500;
export const alternateComparedLineColor: string = Colors.light.hereworksOrange500;

// eslint-disable-next-line no-shadow
export enum MetricPropTypes {
  // eslint-disable-next-line no-unused-vars
  BINARY = 0, PERCENTAGE,
}

export type MetricThreshold = {
  title: string,
  value: string,
  color: string,
}

export interface MetricProps {
  table?: string,
  index?: string,
  field?: string,
  title: string,
  scoreValue: string,
  fullTitle?: string,
  unit?: string,
  factor?: number,
  type?: MetricPropTypes,
  excludeLocation?: boolean,
  thresholds?: MetricThreshold[],
  description?: string,
  aggregationFunction?: AggregationFunction,
}

interface AirQualityGraphProps {
  showComparison: boolean,
  data: ChartDataPoint[],
  dataCompared: ChartDataPoint[],
  title: string,
  unit: string,
  type?: MetricPropTypes,
  alternateData: ChartDataPoint[],
  alternateDataCompared: ChartDataPoint[],
  alternateTitle: string,
  alternateUnit: string,
  alternateType?: MetricPropTypes,
}

const chartTheme = {
  axis: {
    stroke: 'none',
    style: {
      stroke: '#696D70',
      tickLabels: { fill: '#696D70' },
      grid: { stroke: 'none' },
    },
  },
};

const isValidValue = (value: unknown): value is number => typeof value === 'number'
  && Number.isFinite(value);

const scaleValue = (value: number, lineLimit: number, graphLimit: number): number => value
  * (graphLimit / lineLimit);

const Line = (data, stroke: string, lineLimit: number, graphLimit: number) => {
  const normalizedData = (data ?? []).map((point) => ({
    ...point,
    value: isValidValue(point?.value) ? scaleValue(point.value, lineLimit, graphLimit) : null,
  }));

  const solidSegments = [];
  let currentSegment = [];
  normalizedData.forEach((point) => {
    if (isValidValue(point?.value)) {
      currentSegment.push(point);
      return;
    }
    if (currentSegment.length > 0) {
      solidSegments.push(currentSegment);
      currentSegment = [];
    }
  });
  if (currentSegment.length > 0) solidSegments.push(currentSegment);

  const gapLines = [];
  let previousValidPoint = null;
  normalizedData.forEach((point) => {
    const hasValue: boolean = isValidValue(point?.value);
    if (!hasValue) return;
    if (previousValidPoint && point.index - previousValidPoint.index > 1) {
      gapLines.push([previousValidPoint, point]);
    }
    previousValidPoint = point;
  });

  const lines = [
    ...solidSegments.map((segment, index: number) => <VictoryLine
      key={`${stroke}_solid_${index}`}
      data={segment}
      style={{ data: { stroke, strokeWidth: 1.5 } }}
      labelComponent={<VictoryLabel />}
      x='index'
      y='value'
    />),
    ...gapLines.map((linePoints, index: number) => <VictoryLine
      key={`${stroke}_gap_${index}`}
      data={linePoints}
      style={{
        data: {
          stroke,
          strokeWidth: 1.5,
          strokeDasharray: '2,5',
        },
      }}
      labelComponent={<VictoryLabel />}
      x='index'
      y='value'
    />),
  ];

  return lines;
};

const XAxis = (tickValues: number[], tickFormat, dy: number, fill: string) => <VictoryAxis
  crossAxis={false}
  fixLabelOverlap
  style={{ axis: { stroke: 'none' }, tickLabels: { fontSize: 14 } }}
  {...{ tickValues, tickFormat }}
  tickLabelComponent={<VictoryLabel
    style={{
      fill,
      fontSize: 14,
      lineHeight: 11,
      fontFamily: 'Poppins_400Regular',
    }}
    {...{ dy }}
  />}
/>;

const AirQualityGraph = ({
  showComparison, data, dataCompared, title, unit, type, alternateData, alternateDataCompared,
  alternateTitle, alternateUnit, alternateType,
}: AirQualityGraphProps) => {
  const route: string = useRoute().name;
  const [chartWidth, setChartWidth] = useState<number>(1060);
  const chartHeight: number = 291;
  const paddingHorizontal: number = 55;
  const paddingTop: number = 30;
  const paddingBottom: number = 50;
  const [xAxisTickCount, setXAxisTickCount] = useState<number>(7);

  useEffect(() => {
    if (chartWidth < 600) setXAxisTickCount(3);
    else if (chartWidth < 1000) setXAxisTickCount(5);
    else setXAxisTickCount(7);
  }, [chartWidth]);

  const dataAverage = (chartData) => {
    const values: number[] = (chartData ?? [])
      .map((point) => point?.value)
      .filter((value: number) => isValidValue(value));
    if (!values.length) return 0;
    return Math.floor((values.reduce((a, b) => a + b, 0) / values.length) * 10) / 10;
  };

  const dataValues = (chartData) => (chartData ?? []).map((point) => point?.value)
    .filter((value: number) => isValidValue(value));

  const domainLimit: number = type === MetricPropTypes.BINARY ? 1 : Math.min(1.2 * Math.max(
    ...dataValues(data),
    ...dataValues(dataCompared),
    1,
  ), type === MetricPropTypes.PERCENTAGE ? 100 : 100000000);

  const alternateDomainLimit: number = alternateType === MetricPropTypes.BINARY
    ? 1 : Math.min(1.3 * Math.max(
      ...dataValues(alternateData),
      ...dataValues(alternateDataCompared),
      1,
    ), alternateType === MetricPropTypes.PERCENTAGE ? 100 : 100000000);

  const higherDomainLimit = Math.max(domainLimit, alternateDomainLimit);

  const tooltipCircle = (
    x: number,
    value: number,
    stroke: string,
    limit: number,
  ) => isValidValue(value)
    && (
      <circle
        r={3}
        strokeWidth={6}
        strokeOpacity={0.3}
        opacity={1}
        {...{ stroke }}
        cx={x}
        cy={
          paddingTop
          + (chartHeight - paddingBottom - paddingTop - 1)
          * (1 - value / limit)
        }
        fill={stroke}
      />
    );

  class CustomFlyout extends React.Component {
    render() {
      const { x, y, datum } = this.props as any;
      // eslint-disable-next-line no-underscore-dangle
      const index: number = datum._x;

      if ([null, undefined].includes(index)) return null;

      const date: Date = data[index]?.date
        ? new Date(data[index]?.date) : null;
      const dateCompared: Date = dataCompared[index]?.date
        ? new Date(dataCompared[index]?.date) : null;
      const dateText: string = date ? tooltipDateLabel(data, index) : '-';
      const dateComparedText: string = dateCompared
        ? `vs. ${tooltipDateLabel(dataCompared, index)}` : null;
      const valueText: string = `${title}: ${data[index]
        ?.value ? `${Math.floor(data[index]?.value)} ${unit}` : '-'}`;
      const valueComparedText: string = `${title} (compared): ${dataCompared[index]
        ?.value ? `${Math.floor(dataCompared[index]?.value)} ${unit}` : '-'}`;
      const alternateValueText: string = alternateData ? `${alternateTitle}: ${alternateData[index]
        ?.value ? `${Math.floor(alternateData[index]?.value)} ${alternateUnit}` : '-'}` : '';
      const alternateValueComparedText: string = alternateDataCompared
        ? `${alternateTitle} (compared): ${alternateDataCompared[index]
          ?.value ? `${Math.floor(alternateDataCompared[index]?.value)} ${alternateUnit}` : '-'}`
        : '';
      let containerHeight: number = showComparison ? 96 : 56;
      if (alternateData) containerHeight += showComparison ? 40 : 20;
      const containerWidth: number = (showComparison ? 100 : 50)
        + Math.max(
          dateText.length,
          (showComparison && dataCompared[index]?.date)
            ? dateComparedText.length : 0,
          valueText.length,
          alternateValueText.length,
          valueComparedText.length,
          alternateValueComparedText.length,
        ) * 6.5;
      const containerX: number = x > containerWidth + 20 ? x - containerWidth - 10 : x + 10;
      let containerY: number = Math.max(Math.min(y - containerHeight, 210 - containerHeight), 10);
      if (!Number.isFinite(containerY)) containerY = 10;

      const spot = (cy: number, fill: string) => <circle
        r={6}
        cx={containerX + 15}
        cy={containerY + cy}
        {...{ fill }}
      />;

      const label = (cy: number, text: string) => <text
        x={containerX + 30}
        y={containerY + cy}
        fontFamily={'Poppins_500Medium'}
        fontSize={12}
        strokeWidth={0}
        fill={Colors.light.hereworksBlack400}>
        {text}
      </text>;

      const dateLabel = (cy: number, text: string) => <text
        x={containerX + 10}
        y={containerY + cy}
        fontFamily={'Poppins_500Medium'}
        fontSize={12}
        strokeWidth={0}
        fill={Colors.light.hereworksBlack600}>
        {text}
      </text>;

      return <svg>
        <defs>
          <filter id={`f3_${route}`} x={-0.5} y={-1} width='200%' height='250%'>
            <feOffset result='offOut' in='SourceGraphic' dx='0' dy='0' />
            <feColorMatrix result='matrixOut'
              in='offOut'
              type='matrix'
              values='0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.1 0 0 0 0 0 0.2 0' />
            <feGaussianBlur result='blurOut' in='matrixOut' stdDeviation='4' />
            <feBlend in='SourceGraphic' in2='blurOut' mode='normal' result='colored' />
          </filter>
        </defs>
        {tooltipCircle(x, data[index]?.value, selectedLineColor, domainLimit)}
        {tooltipCircle(x, dataCompared[index]?.value, comparisonLineColor, domainLimit)}
        {alternateData && tooltipCircle(
          x,
          alternateData[index]?.value,
          alternateLineColor,
          alternateDomainLimit,
        )}
        {alternateDataCompared && tooltipCircle(
          x,
          alternateDataCompared[index]?.value,
          alternateComparedLineColor,
          alternateDomainLimit,
        )}
        <rect
          x={containerX}
          y={containerY}
          width={containerWidth}
          height={containerHeight}
          stroke={selectedLineColor}
          rx={5}
          fill='white'
          filter={`url(#f3_${route})`}
        />
        {!!date && dateLabel(20, dateText)}
        {showComparison && dataCompared[index]?.date && dateLabel(40, dateComparedText)}
        <line
          x1={x}
          x2={x}
          y1={-10}
          y2={chartHeight - paddingBottom + 10}
          stroke={Colors.light.hereworksBlue100}
          strokeDasharray='10,10'
          strokeWidth={1}
        />
        {spot(showComparison ? 60.5 : 40.5, selectedLineColor)}
        {label(showComparison ? 65 : 45, valueText)}
        {alternateData && <>
          {spot(showComparison ? 80.5 : 60.5, alternateLineColor)}
          {label(showComparison ? 85 : 65, alternateValueText)}
        </>}
        {showComparison && <>
          {spot(alternateData ? 100.5 : 80.5, comparisonLineColor)}
          {label(alternateData ? 105 : 85, valueComparedText)}
          {alternateData && <>
            {spot(120.5, alternateComparedLineColor)}
            {label(125, alternateValueComparedText)}
          </>}
        </>}
      </svg>;
    }
  }

  const dashedLine = (average: number, limit: number, stroke: string) => isValidValue(average)
    && <VictoryLine
    data={[
      { y: average * (higherDomainLimit / limit), x: 0 },
      {
        y: average * (higherDomainLimit / limit),
        x: Math.max(0, (data?.length ?? 1) - 1),
        label: `${average} avg`,
      },
    ]}
    standalone={false}
    labelComponent={<VictoryLabel />}
    style={{
      data: {
        stroke,
        strokeWidth: 1.5,
        strokeDasharray: 8,
        opacity: 0.2,
      },
    }}
  />;

  const yAxisTickFormat = (value: number, limit: number) => Math.round((limit > 2 ? 1 : 10) * value
    * (limit / higherDomainLimit)) / (limit > 2 ? 1 : 10);

  return <View
    onLayout={(e) => setChartWidth(e.nativeEvent.layout.width)}
    style={styles.container}
  >
    <VictoryChart
      domain={{ y: [0, higherDomainLimit] }}
      width={chartWidth}
      height={chartHeight}
      padding={{
        top: paddingTop,
        bottom: paddingBottom,
        left: paddingHorizontal,
        right: paddingHorizontal,
      }}
      containerComponent={<VictoryVoronoiContainer
        labels={({ datum }) => datum}
        voronoiDimension='x'
        labelComponent={<CustomFlyout />}
      />}
      theme={chartTheme}
    >
      {XAxis(
        tickValSamples(
          [...Array(data?.length).keys()],
          Math.min(
            xAxisTickCount,
            Math.floor(xAxisTickCount * ((data?.length ?? 1) / (dataCompared?.length ?? 1))),
          ),
        ),
        (t) => ((data?.length > 0 && data[t]) ? axisDateLabel(data, t) : ''),
        20,
        Colors.light.hereworksBlack400,
      )}
      {showComparison && XAxis(
        tickValSamples(
          [...Array(dataCompared?.length).keys()],
          Math.min(
            xAxisTickCount,
            Math.floor(xAxisTickCount * ((dataCompared?.length ?? 1) / (data?.length ?? 1))),
          ),
        ),
        (t) => ((dataCompared?.length > 0 && dataCompared[t]) ? axisDateLabel(dataCompared, t) : ''),
        36,
        Colors.light.hereworksYellow600,
      )}
      <VictoryAxis
        key='y-axis'
        label={title}
        tickCount={data?.length > 0 ? 5 : 2}
        tickFormat={(value: number) => yAxisTickFormat(value, domainLimit)}
        dependentAxis
        crossAxis={false}
        fixLabelOverlap
        style={{
          axis: { stroke: 'none' },
          tickLabels: { fontSize: 14 },
          grid: {
            stroke: Colors.light.hereworksWhite500,
            strokeWidth: 1,
            pointerEvents: 'none',
          },
        }}
        containerComponent={<VictoryVoronoiContainer />}
        axisLabelComponent={<VictoryLabel
          style={{
            fill: Colors.light.hereworksBlack200,
            fontSize: 14,
            lineHeight: 11,
            fontFamily: 'Poppins_400Regular',
          }}
          textAnchor='start'
          angle={0}
          y={20}
          x={0}
        />}
        tickLabelComponent={<VictoryLabel
          style={{
            fill: Colors.light.hereworksBlack400,
            fontSize: 14,
            lineHeight: 11,
            fontFamily: 'Poppins_400Regular',
          }}
          dx={-paddingHorizontal}
          textAnchor='start'
        />}
        offsetX={paddingHorizontal}
      />
      {alternateData && <VictoryAxis
        key='y-axis-alternate'
        label={alternateTitle}
        tickCount={alternateData?.length > 0 ? 5 : 2}
        tickFormat={(value: number) => yAxisTickFormat(value, alternateDomainLimit)}
        dependentAxis
        crossAxis={false}
        fixLabelOverlap
        offsetX={chartWidth - 5}
        style={{
          axis: { stroke: 'none' },
          tickLabels: { fontSize: 14 },
        }}
        containerComponent={<VictoryVoronoiContainer />}
        axisLabelComponent={<VictoryLabel
          style={{
            fill: Colors.light.hereworksBlack200,
            fontSize: 14,
            lineHeight: 11,
            fontFamily: 'Poppins_400Regular',
          }}
          textAnchor='end'
          angle={0}
          y={20}
          x={chartWidth}
        />}
        tickLabelComponent={<VictoryLabel
          style={{
            fill: Colors.light.hereworksBlack400,
            fontSize: 14,
            lineHeight: 11,
            fontFamily: 'Poppins_400Regular',
          }}
          textAnchor='end'
        />}
      />}
      {dashedLine(dataAverage(data), domainLimit, selectedLineColor)}
      {alternateData
        && dashedLine(dataAverage(alternateData), alternateDomainLimit, alternateLineColor)}
      {showComparison && dashedLine(dataAverage(dataCompared), domainLimit, comparisonLineColor)}
      {alternateDataCompared && showComparison && dashedLine(
        dataAverage(alternateDataCompared),
        alternateDomainLimit,
        alternateComparedLineColor,
      )}
      {Line(data, selectedLineColor, domainLimit, higherDomainLimit)}
      {alternateData
        && Line(alternateData, alternateLineColor, alternateDomainLimit, higherDomainLimit)}
      {Line(dataCompared, comparisonLineColor, domainLimit, higherDomainLimit)}
      {alternateDataCompared && Line(
        alternateDataCompared,
        alternateComparedLineColor,
        alternateDomainLimit,
        higherDomainLimit,
      )}
    </VictoryChart>
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});

export default AirQualityGraph;
